import i18n from "@/i18n";
// The formatter lives with the store-price arithmetic (lib/payments/prices.ts)
// because that module is React-Native-free and therefore testable; our own
// three currencies are just a narrower case of the same job.
import { formatAmountInCurrency } from "@/lib/payments/prices";

export const PLUS_MONTHLY_PRICE = 7.99;
// Annual is a flat pinned price (not a multiple of the monthly), charged as the
// SAME number in every currency. 69.99 vs 12×7.99 = 95.88 ⇒ ~27% saving.
export const PLUS_ANNUAL_PRICE = 69.99;
export const PLUS_ANNUAL_SAVINGS_PERCENT = Math.round(
  (1 - PLUS_ANNUAL_PRICE / (PLUS_MONTHLY_PRICE * 12)) * 100,
);

export type LocalPlusCurrency = "GBP" | "EUR" | "USD";

/**
 * WHICH currency to show is the server's decision, made from the caller's
 * actual location (GET /billing/currency — see `lib/billing-region.ts`).
 * These helpers only format an amount in a currency they are handed. The old
 * locale→currency derivation is gone on purpose: the device locale describes
 * language, not location, so "English (UK)" showed pounds from anywhere.
 */
export function plusCurrencySymbol(currency: LocalPlusCurrency): string {
  return currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
}

export function formatPlusPrice(
  amount: number,
  currency: LocalPlusCurrency,
): string {
  const formatted = formatAmountInCurrency(amount, currency);
  // Our own three currencies have a symbol we know, so a failed Intl lookup can
  // still render "£7.99" rather than "7.99 GBP".
  return formatted.endsWith(currency)
    ? `${plusCurrencySymbol(currency)}${amount.toFixed(2)}`
    : formatted;
}

export function getLocalPlusPricing(currency: LocalPlusCurrency) {
  const annual = PLUS_ANNUAL_PRICE;
  return {
    symbol: plusCurrencySymbol(currency),
    monthly: formatPlusPrice(PLUS_MONTHLY_PRICE, currency),
    annual: formatPlusPrice(annual, currency),
    monthlyEquivalentAnnual: formatPlusPrice(annual / 12, currency),
    savingsPercent: PLUS_ANNUAL_SAVINGS_PERCENT,
  };
}

export function getFreePlanFeatures(): string[] {
  return [
    i18n.t("billing.features.free.cues"),
    i18n.t("billing.features.free.conversations"),
    i18n.t("billing.features.free.groups"),
    i18n.t("billing.features.free.deck"),
    i18n.t("billing.features.free.practice"),
    i18n.t("billing.features.free.goals"),
    i18n.t("billing.features.free.reflections"),
    i18n.t("billing.features.free.mood"),
    i18n.t("billing.features.free.progressCard"),
    i18n.t("billing.features.free.safety"),
  ];
}

export function getPlusPlanFeatures(): string[] {
  return [
    i18n.t("billing.features.plus.everythingInFree"),
    i18n.t("billing.features.plus.cues"),
    // Go deeper, Make a plan and Wrap up are Plus decks — the server filters
    // them out of a free user's deck entirely, so this is a real difference in
    // what the two plans receive, not a UI lock. The free line above had to
    // stop saying "full library" on the same day.
    i18n.t("billing.features.plus.deck"),
    i18n.t("billing.features.plus.practice"),
    i18n.t("billing.features.plus.feedback"),
    i18n.t("billing.features.plus.goals"),
    i18n.t("billing.features.plus.reflections"),
    i18n.t("billing.features.plus.dashboard"),
    i18n.t("billing.features.plus.history"),
    i18n.t("billing.features.plus.customisation"),
    i18n.t("billing.features.plus.export"),
  ];
}
